import React, { useState, useEffect } from "react";
import { LoadCanvasTemplate, loadCaptchaEnginge, validateCaptcha } from "react-simple-captcha";
import { toast } from "react-hot-toast";
import PageHead from "@/components/common/PageHead";
import { submitContactForm, ContactFormData } from "@/services/ContactService";

const Contact = () => {
    const [formData, setFormData] = useState<ContactFormData>({
        name: "",
        email: "",
        subject: "",
        message: "",
    });
    const [captchaInput, setCaptchaInput] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [captchaValidated, setCaptchaValidated] = useState(false);

    // Load captcha on component mount
    useEffect(() => {
        loadCaptchaEnginge(6); // 6 is the captcha length
    }, []);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleCaptchaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setCaptchaInput(e.target.value);
    };

    const handleValidateCaptcha = () => {
        if (validateCaptcha(captchaInput)) {
            setCaptchaValidated(true);
            return true;
        } else {
            setCaptchaValidated(false);
            toast.error("Invalid captcha. Please try again.");
            setCaptchaInput("");
            loadCaptchaEnginge(6); // Reload captcha on failure
            return false;
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!captchaValidated) {
            toast.error("Please validate the captcha first");
            return;
        }

        setIsSubmitting(true);

        try {
            await submitContactForm(formData);
            setIsSubmitted(true);
            toast.success("Your message has been sent!");
            // Reset form
            setFormData({
                name: "",
                email: "",
                subject: "",
                message: "",
            });
            setCaptchaInput("");
            setCaptchaValidated(false);
            // Reload captcha
            loadCaptchaEnginge(6);
        } catch (error: any) {
            let errorMessage = "Failed to send message. Please try again.";
            if (error && error.response && error.response.data) {
                errorMessage = error.response.data.message || errorMessage;
            } else if (error && error.message) {
                errorMessage = error.message;
            }
            toast.error(errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <PageHead
                title="Contact Us | PriceMe"
                description="Have a question, idea, or just want to say hi? We'd love to hear from you."
            />
            <div className="tw-p-4 tw-max-w-[600px] tw-mx-auto tw-min-h-[calc(100vh-9svh)] tw-py-8 md:tw-pt-8">
                <div className="tw-font-['Noto_Sans',Arial,sans-serif]">
                    <h1 className="tw-text-2xl md:tw-text-[28px] tw-font-bold tw-mb-6 tw-text-center tw-text-[var(--onyx)]">
                        Contact us
                    </h1>

                    <div className="tw-mb-8 tw-text-[#666666] tw-text-sm tw-leading-relaxed">
                        <p className="tw-mb-4 last:tw-mb-0">Have a question, idea, or just want to say hi? We'd love to hear from you.</p>
                        <p className="tw-mb-4 last:tw-mb-0">
                            To share feedback about the app, submit a listing for us to feature, or suggest a
                            partnership, just fill out the form below
                        </p>
                    </div>

                    <form onSubmit={handleSubmit} className="tw-mb-8">
                            <div className="tw-mb-6">
                                <label htmlFor="name" className="tw-block tw-mb-2 tw-font-medium tw-text-[var(--onyx)] tw-text-sm">
                                    Your name
                                </label>
                                <input
                                    type="text"
                                    id="name"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    required
                                    disabled={isSubmitting}
                                    className="tw-w-full tw-p-3 tw-border tw-border-[var(--grey)] tw-rounded tw-text-sm tw-font-['Noto_Sans',Arial,sans-serif] tw-text-[var(--onyx)] tw-box-border focus:tw-outline-none focus:tw-border-[var(--purple-heart)] disabled:tw-bg-[#f5f5f5] disabled:tw-cursor-not-allowed"
                                />
                            </div>

                            <div className="tw-mb-6">
                                <label htmlFor="email" className="tw-block tw-mb-2 tw-font-medium tw-text-[var(--onyx)] tw-text-sm">
                                    Your email
                                </label>
                                <input
                                    type="email"
                                    id="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    required
                                    disabled={isSubmitting}
                                    className="tw-w-full tw-p-3 tw-border tw-border-[var(--grey)] tw-rounded tw-text-sm tw-font-['Noto_Sans',Arial,sans-serif] tw-text-[var(--onyx)] tw-box-border focus:tw-outline-none focus:tw-border-[var(--purple-heart)] disabled:tw-bg-[#f5f5f5] disabled:tw-cursor-not-allowed"
                                />
                            </div>

                            <div className="tw-mb-6">
                                <label htmlFor="subject" className="tw-block tw-mb-2 tw-font-medium tw-text-[var(--onyx)] tw-text-sm">
                                    Subject
                                </label>
                                <input
                                    type="text"
                                    id="subject"
                                    name="subject"
                                    value={formData.subject}
                                    onChange={handleInputChange}
                                    required
                                    disabled={isSubmitting}
                                    className="tw-w-full tw-p-3 tw-border tw-border-[var(--grey)] tw-rounded tw-text-sm tw-font-['Noto_Sans',Arial,sans-serif] tw-text-[var(--onyx)] tw-box-border focus:tw-outline-none focus:tw-border-[var(--purple-heart)] disabled:tw-bg-[#f5f5f5] disabled:tw-cursor-not-allowed"
                                />
                            </div>

                            <div className="tw-mb-6">
                                <label htmlFor="message" className="tw-block tw-mb-2 tw-font-medium tw-text-[var(--onyx)] tw-text-sm">
                                    Your message
                                </label>
                                <textarea
                                    id="message"
                                    name="message"
                                    value={formData.message}
                                    onChange={handleInputChange}
                                    rows={6}
                                    required
                                    disabled={isSubmitting}
                                    className="tw-w-full tw-p-3 tw-border tw-border-[var(--grey)] tw-rounded tw-text-sm tw-font-['Noto_Sans',Arial,sans-serif] tw-text-[var(--onyx)] tw-box-border focus:tw-outline-none focus:tw-border-[var(--purple-heart)] disabled:tw-bg-[#f5f5f5] disabled:tw-cursor-not-allowed tw-resize-y tw-min-h-[120px]"
                                />
                            </div>

                            <div className="tw-mb-6">
                                <LoadCanvasTemplate />
                                <div className="tw-flex tw-gap-2 tw-mt-2 tw-flex-col md:tw-flex-row md:tw-items-center">
                                    <input
                                        type="text"
                                        placeholder="Enter captcha"
                                        value={captchaInput}
                                        onChange={handleCaptchaChange}
                                        disabled={isSubmitting || captchaValidated}
                                        className={`tw-flex-1 md:tw-max-w-[200px] tw-w-full tw-p-3 tw-border tw-border-[var(--grey)] tw-rounded tw-text-sm tw-font-['Noto_Sans',Arial,sans-serif] tw-text-[var(--onyx)] tw-box-border focus:tw-outline-none focus:tw-border-[var(--purple-heart)] disabled:tw-bg-[#f5f5f5] disabled:tw-cursor-not-allowed ${
                                            captchaValidated
                                                ? "tw-border-[#4caf50] tw-bg-[#f1f8f4]"
                                                : ""
                                        }`}
                                    />
                                    {!captchaValidated && (
                                        <button
                                            type="button"
                                            onClick={handleValidateCaptcha}
                                            disabled={isSubmitting || !captchaInput}
                                            className="tw-px-4 tw-py-2 tw-bg-[var(--purple-heart)] tw-text-white tw-border-none tw-rounded tw-cursor-pointer tw-text-sm tw-font-medium tw-transition-colors hover:tw-bg-[var(--meteorite)] disabled:tw-bg-[#ccc] disabled:tw-cursor-not-allowed md:tw-w-auto tw-w-full"
                                        >
                                            Validate
                                        </button>
                                    )}
                                    {captchaValidated && (
                                        <span className="tw-text-[#4caf50] tw-font-medium tw-text-sm">✓ Validated</span>
                                    )}
                                </div>
                            </div>

                            <button
                                type="submit"
                                disabled={isSubmitting || !captchaValidated || isSubmitted}
                                className="tw-w-full tw-py-3.5 tw-px-4 tw-bg-[var(--purple-heart)] tw-text-white tw-border-none tw-rounded tw-text-base tw-font-semibold tw-cursor-pointer tw-transition-colors tw-font-['Noto_Sans',Arial,sans-serif] hover:tw-bg-[var(--meteorite)] disabled:tw-bg-[#ccc] disabled:tw-cursor-not-allowed"
                            >
                                {isSubmitted ? "Your message has been sent" : isSubmitting ? "Sending..." : "Send Message"}
                            </button>
                        </form>

                    <div className="tw-mt-8 tw-text-[#666666] tw-text-sm tw-leading-relaxed">
                        <p className="tw-mb-4 last:tw-mb-0">We do our best to respond within 1–2 business days.</p>
                        <p className="tw-mb-4 last:tw-mb-0">
                            For press, partnerships, or business inquiries, please include a short note in your message
                            with the subject line <strong>&quot;Partnership Inquiry&quot;</strong> or <strong>&quot;Press Inquiry.&quot;</strong>
                        </p>
                        <p className="tw-mt-6 tw-font-medium tw-mb-0">Thanks for checking out PriceMe!</p>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Contact;
